/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  CloseOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Modal, Alert, Checkbox } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useState, useEffect, useMemo } from 'react'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import BannerController from '../Banner/BannerController'
import Banner from '../Banner'

interface DeleteCompetitionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (shouldForce?: boolean) => Promise<void>
  title: string
  message: string
  secondaryMessage?: string
  itemName?: string
  buttonText?: string
  useForceDelete?: boolean
  forceDeleteText?: string
  displayBanner?: boolean
  bannerText?: string
  // Backward compatibility props
  unmountOnClose?: boolean
  shouldCloseModalAfterDelete?: boolean
  disabled?: boolean
}

/**
 * Modern delete competition modal component following the patterns from DeleteConfirmationModal
 * but adapted for competition/schedule deletion with force delete functionality
 */
const DeleteCompetitionModal: React.FC<DeleteCompetitionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  secondaryMessage,
  itemName,
  buttonText = 'DELETE',
  useForceDelete = false,
  forceDeleteText,
  displayBanner = true,
  bannerText,
  unmountOnClose = false,
  shouldCloseModalAfterDelete = true,
  disabled = false,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  // State management
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shouldForce, setShouldForce] = useState(false)
  const [shouldShowForceMessage, setShouldShowForceMessage] = useState(false)

  /**
   * Handles modal close and resets state
   */
  const handleClose = useCallback(() => {
    if (isDeleting) {
      return
    } // Prevent closing while deleting

    setShouldForce(false)
    setShouldShowForceMessage(false)
    setError(null)
    onClose()
  }, [onClose, isDeleting])

  /**
   * Handles deletion confirmation
   */
  const handleConfirm = useCallback(async () => {
    setIsDeleting(true)
    setError(null)

    try {
      await onConfirm(shouldForce)

      // Show success banner
      if (displayBanner) {
        BannerController.add(
          ({
            duration = 5000,
            onClose = () => {},
            onClick = () => {},
            onMouseEnter = () => {},
            onMouseLeave = () => {},
            subChildren = null,
            ...props
          }) => (
            <Banner
              {...props}
              duration={duration}
              onClose={onClose}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              subChildren={subChildren}
            >
              {bannerText || 'Deletion successful'}
            </Banner>
          )
        )
      }

      // Only close modal if shouldCloseModalAfterDelete is true
      if (shouldCloseModalAfterDelete) {
        handleClose()
      }
    } catch (e: any) {
      setError(e.message || 'Failed to delete')
      setIsDeleting(false)

      // Show force delete option for 409 conflicts
      if (useForceDelete && e.status === 409) {
        setShouldShowForceMessage(true)
      }
    }
  }, [
    onConfirm,
    shouldForce,
    displayBanner,
    bannerText,
    handleClose,
    useForceDelete,
  ])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShouldForce(false)
      setShouldShowForceMessage(false)
      setError(null)
      setIsDeleting(false)
    }
  }, [isOpen])

  // Don't render if disabled or if unmountOnClose and modal is closed
  if (disabled || (unmountOnClose && !isOpen)) {
    return null
  }

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.ERROR_200};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          {title}
        </Text>

        {/* Message */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              {message}
              {itemName && (
                <>
                  {' '}
                  <span
                    css={css`
                      font-weight: 700;
                    `}
                  >
                    {itemName}
                  </span>
                </>
              )}
              {secondaryMessage && (
                <>
                  <br />
                  <br />
                  {secondaryMessage}
                </>
              )}
            </Text>
          </div>
        </div>

        {/* Force Delete Checkbox */}
        {shouldShowForceMessage && (
          <div
            css={css`
              align-self: stretch;
              background: ${colors.SECONDARY_200};
              border-radius: 8px;
              border: 1px solid ${colors.SECONDARY_100};
              padding: 16px;
              display: flex;
              flex-direction: column;
              gap: 12px;
            `}
          >
            <Checkbox
              checked={shouldForce}
              onChange={(e) => setShouldForce(e.target.checked)}
              disabled={isDeleting}
              css={css`
                .ant-checkbox-inner {
                  background-color: ${colors.SECONDARY_200};
                  border-color: ${colors.SECONDARY_100};
                }
                .ant-checkbox-checked .ant-checkbox-inner {
                  background-color: ${colors.ERROR_200};
                  border-color: ${colors.ERROR_200};
                }
                .ant-checkbox + span {
                  color: ${colors.WHITE};
                  font-size: 14px;
                  font-weight: 500;
                }
              `}
            >
              Force Delete (Ignore warnings)
            </Checkbox>
            {shouldForce && forceDeleteText && (
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                {forceDeleteText}
              </Text>
            )}
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
              background: ${colors.ERROR_50};
              border: 1px solid ${colors.ERROR_200};
              .ant-alert-message {
                color: ${colors.ERROR_400};
              }
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              background: ${colors.ERROR_200};
              border-radius: 8px;
              border: 2px solid ${colors.ERROR_200};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${!isDeleting ? 'pointer' : 'not-allowed'};
              opacity: ${!isDeleting ? 1 : 0.5};
              transition: all 0.2s;
              &:hover {
                opacity: ${!isDeleting ? 0.8 : 0.5};
              }
            `}
            onClick={!isDeleting ? handleConfirm : undefined}
            disabled={isDeleting}
          >
            {!isDeleting && (
              <DeleteOutlined
                css={css`
                  font-size: 16px;
                  color: ${colors.WHITE};
                `}
              />
            )}
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 16px;
                font-weight: 600;
                line-height: 20px;
              `}
            >
              {isDeleting
                ? `${buttonText.toUpperCase()}...`
                : buttonText.toUpperCase()}
            </Text>
          </button>
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={handleClose}
            disabled={isDeleting}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 16px;
                font-weight: 600;
                line-height: 20px;
              `}
            >
              CANCEL
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteCompetitionModal
